/**
 * KiCKS integration — the club's review inbox and team pairing.
 *
 * Every match a paired coach finishes in the KiCKS app arrives here as an
 * entry. NOTHING reaches the website until the admin applies a part:
 *   Result   → the fixture (claimed or created)
 *   Players  → the squad mapping, the lineup and per-player stats
 *   Report   → a news article, or not
 * See docs/features/kicks-integration.md.
 */
import { useState, useEffect, useCallback, FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getKicksInbox, getKicksInboxEntry, saveKicksInboxEntry, applyKicksInboxEntry, dismissKicksInboxEntry,
  getKicksLinks, createKicksLink, deleteKicksLink, getTeams,
  KicksInboxEntry, KicksInboxPlayer, KicksSquadPlayer, KicksLink, KicksRole, Team,
} from '../api/client';
import './KicksIntegration.css';

const ROLE_LABEL: Record<KicksRole, string> = { started: 'Started', sub: 'Sub', unused: 'Did not play' };
const fmt = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const fmtDate = (d: string) => { const x = new Date(`${d}T00:00:00`); return isNaN(+x) ? d : x.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); };
const playerName = (p: KicksSquadPlayer) => `${p.firstName} ${p.lastName}`.trim();

// ── Inbox list ───────────────────────────────────────────────

function InboxList() {
  const [entries, setEntries] = useState<KicksInboxEntry[]>([]);
  const [links, setLinks] = useState<KicksLink[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [inbox, l, t] = await Promise.all([getKicksInbox(), getKicksLinks(), getTeams()]);
      setEntries(inbox.entries); setLinks(l.links); setTeams(t); setError('');
    } catch (e: any) { setError(e?.message || 'Could not load the KiCKS inbox'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const teamName = (id: string) => teams.find(t => t.id === id)?.name || id;
  const pending = entries.filter(e => e.status === 'pending');
  const decided = entries.filter(e => e.status !== 'pending');

  return (
    <div className="tm-page-shell kicks-page">
      <div className="page-header">
        <h1>KiCKS integration</h1>
        <p className="page-subtitle">
          Results, stats and match reports sent from the KiCKS coaching app. Nothing goes on your website until you apply it here.
        </p>
      </div>
      {error && <div className="kicks-error">{error}</div>}

      <ConnectPanel links={links} teams={teams} onChanged={load} />

      <section className="kicks-section">
        <h2>To review {pending.length > 0 && <span className="kicks-count">{pending.length}</span>}</h2>
        {loading ? <p className="kicks-muted">Loading…</p>
          : pending.length === 0 ? <p className="kicks-muted">Nothing waiting. When a paired coach finishes a match it will appear here.</p>
          : <ul className="kicks-list">{pending.map(e => <EntryRow key={e.id} e={e} teamName={teamName(e.teamId)} />)}</ul>}
      </section>

      {decided.length > 0 && (
        <section className="kicks-section">
          <h2>Decided</h2>
          <ul className="kicks-list kicks-list-muted">{decided.map(e => <EntryRow key={e.id} e={e} teamName={teamName(e.teamId)} />)}</ul>
        </section>
      )}
    </div>
  );
}

function EntryRow({ e, teamName }: { e: KicksInboxEntry; teamName: string }) {
  const r = e.result;
  const parts = [
    e.applied.result ? 'result applied' : null,
    e.applied.stats ? 'stats applied' : null,
    e.applied.report ? `report ${e.applied.report}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <li className={`kicks-row kicks-row-${e.status}`}>
      <Link to={`/kicks/${encodeURIComponent(e.id)}`} className="kicks-row-link">
        <div className="kicks-row-main">
          <span className="kicks-row-score">{r.goalsFor}–{r.goalsAgainst}</span>
          <span className="kicks-row-opp">{r.homeAway === 'home' ? 'v' : 'at'} {r.opponent}</span>
          {e.isUpdate && <span className="badge badge-warning">Update</span>}
          {e.report && <span className="badge badge-info">Report</span>}
        </div>
        <div className="kicks-row-meta">
          {teamName} · {fmtDate(r.date)}{r.koTime ? ` ${r.koTime}` : ''} · {e.players.length} players · received {fmt(e.receivedAt)}
          {parts ? ` · ${parts}` : ''}
        </div>
      </Link>
    </li>
  );
}

// ── Pairing ──────────────────────────────────────────────────

function ConnectPanel({ links, teams, onChanged }: { links: KicksLink[]; teams: Team[]; onChanged: () => void }) {
  const [code, setCode] = useState('');
  const [teamId, setTeamId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const free = teams.filter(t => !links.some(l => l.teamId === t.id));

  const submit = async (ev: FormEvent) => {
    ev.preventDefault(); setBusy(true); setMsg(null);
    try {
      const r = await createKicksLink(code.trim(), teamId);
      setMsg({ ok: true, text: `Connected ${r.link.teamName} to ${r.link.kicksCoachName}'s KiCKS team${r.link.kicksTeamName ? ` "${r.link.kicksTeamName}"` : ''}.` });
      setCode(''); setTeamId(''); onChanged();
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not connect' }); }
    finally { setBusy(false); }
  };
  const disconnect = async (l: KicksLink) => {
    if (!confirm(`Disconnect ${l.teamName} from KiCKS? Matches will stop arriving here. You can reconnect any time with a new code.`)) return;
    try { await deleteKicksLink(l.teamId); onChanged(); }
    catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not disconnect' }); }
  };

  return (
    <section className="card kicks-section kicks-connect">
      <h2>Connected teams</h2>
      {links.length === 0
        ? <p className="kicks-muted">No team is connected yet.</p>
        : <ul className="kicks-links">{links.map(l => (
            <li key={l.teamId}>
              <strong>{l.teamName}</strong> ← {l.kicksCoachName || 'a coach'}{l.kicksTeamName ? ` · "${l.kicksTeamName}"` : ''}
              <span className="kicks-muted"> · since {fmt(l.pairedAt)}</span>
              <button type="button" className="ebtn ebtn-secondary ebtn-sm" onClick={() => disconnect(l)}>Disconnect</button>
            </li>))}</ul>}
      <form className="kicks-connect-form" onSubmit={submit}>
        <p className="kicks-muted">
          The coach opens KiCKS → Settings → <em>Connect to grassrootsfc</em> and sends you a code. Paste it here and choose which of your teams it is.
        </p>
        <div className="kicks-connect-fields">
          <input className="form-input" value={code} onChange={e => setCode(e.target.value)} placeholder="Pairing code from the coach" required
            pattern="[0-9a-fA-F-]{36}" title="A 36-character code" />
          <select className="form-select" value={teamId} onChange={e => setTeamId(e.target.value)} required>
            <option value="">Which team?</option>
            {free.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button type="submit" className="ebtn ebtn-primary" disabled={busy || !code || !teamId}>{busy ? 'Connecting…' : 'Connect'}</button>
        </div>
        {msg && <p className={msg.ok ? 'kicks-ok' : 'kicks-error'}>{msg.text}</p>}
      </form>
    </section>
  );
}

// ── Decision strip ───────────────────────────────────────────

function DecisionStrip({ entry }: { entry: KicksInboxEntry }) {
  const a = entry.applied;
  const hasPlayers = entry.players.some(p => !p.dropped);
  const steps: Array<{ label: string; state: 'done' | 'todo' | 'na'; text: string }> = [
    { label: 'Result', state: a.result ? 'done' : 'todo', text: a.result ? 'on the website' : 'waiting for you' },
    { label: 'Players & stats', state: a.stats ? 'done' : hasPlayers ? 'todo' : 'na', text: a.stats ? 'on the website' : hasPlayers ? (a.result ? 'waiting for you' : 'after the result') : 'none sent' },
    { label: 'Report', state: a.report ? 'done' : entry.report ? 'todo' : 'na', text: a.report === 'published' ? 'published' : a.report === 'skipped' ? 'not published' : entry.report ? (a.result ? 'waiting for you' : 'after the result') : 'none yet' },
  ];
  const left = steps.filter(s => s.state === 'todo').length;
  return (
    <div className="kicks-strip">
      {steps.map(s => (
        <div key={s.label} className={`kicks-step kicks-step-${s.state}`}>
          <span className="kicks-step-dot" aria-hidden="true">{s.state === 'done' ? '✓' : s.state === 'na' ? '–' : ''}</span>
          <span className="kicks-step-label">{s.label}</span>
          <span className="kicks-step-text">{s.text}</span>
        </div>
      ))}
      <div className="kicks-strip-note">
        {entry.status === 'pending'
          ? (left ? `${left} decision${left === 1 ? '' : 's'} left — this match leaves To review when all are made, or when you dismiss it.` : 'All decided.')
          : entry.status === 'applied' ? 'All decided — this match is done.' : 'Dismissed — nothing was applied.'}
      </div>
    </div>
  );
}

// ── Entry review ─────────────────────────────────────────────

function EntryReview() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<KicksInboxEntry | null>(null);
  const [squad, setSquad] = useState<KicksSquadPlayer[]>([]);
  const [suggestedFixture, setSuggestedFixture] = useState<any>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([getKicksInboxEntry(id), getTeams()]);
      setEntry(r.entry); setSquad(r.squad); setSuggestedFixture(r.suggestedFixture); setTeams(t); setDirty(false);
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not load this entry' }); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!entry) return <div className="tm-page-shell kicks-page"><Link to="/kicks">← KiCKS inbox</Link><p className="kicks-muted">{msg?.text || 'Loading…'}</p></div>;

  const pending = entry.status === 'pending';
  const team = teams.find(t => t.id === entry.teamId);
  const setResult = (patch: Partial<KicksInboxEntry['result']>) => { setEntry({ ...entry, result: { ...entry.result, ...patch } }); setDirty(true); };
  const setPlayer = (k: string, patch: Partial<KicksInboxPlayer>) => {
    setEntry({ ...entry, players: entry.players.map(p => p.kicksPlayerId === k ? { ...p, ...patch } : p) }); setDirty(true);
  };
  const setReport = (patch: { title?: string; text?: string }) => { if (entry.report) { setEntry({ ...entry, report: { ...entry.report, ...patch } }); setDirty(true); } };

  const save = async () => {
    setBusy('save'); setMsg(null);
    try {
      const r = await saveKicksInboxEntry(entry.id, {
        result: entry.result,
        players: entry.players.map(p => ({ kicksPlayerId: p.kicksPlayerId, playerId: p.playerId, dropped: p.dropped, role: p.role, goals: p.goals, assists: p.assists, saves: p.saves, defensiveActions: p.defensiveActions })),
        report: entry.report ? { title: entry.report.title, text: entry.report.text } : undefined,
      });
      setEntry(r.entry); setDirty(false); setMsg({ ok: true, text: 'Edits saved. They will be used when you apply — the match stays in To review until every card is decided.' });
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not save' }); }
    finally { setBusy(''); }
  };

  const apply = async (parts: { result?: boolean; stats?: boolean; report?: 'publish' | 'skip' }, label: string) => {
    if (dirty) { await save(); }
    setBusy(label); setMsg(null);
    try {
      const r = await applyKicksInboxEntry(entry.id, parts);
      const bits = [
        r.fixtureId ? (r.fixtureCreated ? 'fixture created' : 'fixture updated') : null,
        r.lineupSize !== undefined ? `${r.lineupSize} in the lineup` : null,
        r.playersCreated?.length ? `${r.playersCreated.length} new player${r.playersCreated.length === 1 ? '' : 's'} added (hidden from the website until you choose otherwise)` : null,
        r.playersOverPlanLimit?.length ? `not added (plan limit): ${r.playersOverPlanLimit.join(', ')}` : null,
        r.playersIncremented ? `${r.playersIncremented} player card${r.playersIncremented === 1 ? '' : 's'} updated` : (r.statsMode === 'matches' && r.playersUpdated ? `${r.playersUpdated} player cards recomputed` : null),
        r.articleSection ? 'article published' : null,
        parts.report === 'skip' ? 'report not published' : null,
      ].filter(Boolean).join(' · ');
      setMsg({ ok: true, text: `Applied — ${bits || 'done'}.` + (parts.result && !parts.stats && entry.players.length ? ' Next: players & stats.' : '') });
      await load();
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not apply' }); }
    finally { setBusy(''); }
  };

  const dismiss = async () => {
    if (!confirm('Dismiss this entry? Nothing will be applied to the website. The coach can send the match again.')) return;
    setBusy('dismiss');
    try { await dismissKicksInboxEntry(entry.id); navigate('/kicks'); }
    catch (e: any) { setMsg({ ok: false, text: e?.message || 'Could not dismiss' }); setBusy(''); }
  };

  const r = entry.result;
  const activePlayers = entry.players.filter(p => !p.dropped);

  return (
    <div className="tm-page-shell kicks-page">
      <Link to="/kicks" className="kicks-back">← KiCKS inbox</Link>
      <div className="page-header">
        <h1>{r.goalsFor}–{r.goalsAgainst} {r.homeAway === 'home' ? 'v' : 'at'} {r.opponent}</h1>
        <p className="page-subtitle">
          {team?.name || entry.teamId} · received {fmt(entry.receivedAt)}
          {entry.isUpdate && <span className="badge badge-warning">Update to a match you already applied</span>}
          {!pending && <span className="badge badge-info">{entry.status}</span>}
        </p>
      </div>
      {msg && <div className={`kicks-toast ${msg.ok ? 'kicks-toast-ok' : 'kicks-toast-err'}`} role="status">{msg.text}</div>}

      <DecisionStrip entry={entry} />

      {pending && (
        <div className="kicks-approve-all">
          <div>
            <strong>Happy with all of it?</strong>
            <span className="kicks-muted"> Applies the result, the players &amp; stats{entry.report ? ' and publishes the report' : ''} in one go. Or decide card by card below.</span>
          </div>
          <button className="ebtn ebtn-primary" disabled={!!busy}
            onClick={() => apply({ result: true, ...(entry.players.length ? { stats: true } : {}), ...(entry.report ? { report: 'publish' as const } : {}) }, 'all')}>
            {busy === 'all' ? 'Applying…' : 'Approve everything'}
          </button>
        </div>
      )}

      {/* Result */}
      <section className={`card kicks-card ${entry.applied.result ? 'kicks-card-done' : ''}`}>
        <div className="kicks-card-head">
          <h2>Result</h2>
          {entry.applied.result ? <span className="badge badge-success">On the website ✓</span>
            : pending && <button className="ebtn ebtn-primary ebtn-sm" disabled={!!busy} onClick={() => apply({ result: true }, 'result')}>{busy === 'result' ? 'Applying…' : 'Apply result'}</button>}
        </div>
        <div className="kicks-grid">
          <label>Date<input className="form-input" type="date" value={r.date} disabled={!pending} onChange={e => setResult({ date: e.target.value })} /></label>
          <label>Kick-off<input className="form-input" type="time" value={r.koTime} disabled={!pending} onChange={e => setResult({ koTime: e.target.value })} /></label>
          <label>Opponent<input className="form-input" value={r.opponent} disabled={!pending} onChange={e => setResult({ opponent: e.target.value })} /></label>
          <label>Home / away<select className="form-select" value={r.homeAway} disabled={!pending} onChange={e => setResult({ homeAway: e.target.value as 'home' | 'away' })}><option value="home">Home</option><option value="away">Away</option></select></label>
          <label>Us<input className="form-input" type="number" min={0} max={99} value={r.goalsFor} disabled={!pending} onChange={e => setResult({ goalsFor: Number(e.target.value) })} /></label>
          <label>Them<input className="form-input" type="number" min={0} max={99} value={r.goalsAgainst} disabled={!pending} onChange={e => setResult({ goalsAgainst: Number(e.target.value) })} /></label>
        </div>
        {suggestedFixture && !entry.applied.result && (
          <div className="kicks-choice">
            <label className="kicks-radio">
              <input type="radio" name="fixtureChoice" disabled={!pending} checked={r.fixtureChoice !== 'new'} onChange={() => setResult({ fixtureChoice: 'suggested' })} />
              <span>Apply to your existing fixture <strong>{fmtDate(suggestedFixture.date)} v {suggestedFixture.opponent}</strong> ({suggestedFixture.status}{suggestedFixture.source === 'fulltime' ? ', from FA Full-Time' : ''}{suggestedFixture.goalsFor !== null ? `, currently ${suggestedFixture.goalsFor}–${suggestedFixture.goalsAgainst}` : ''}). A kick-off already on it is kept.</span>
            </label>
            <label className="kicks-radio">
              <input type="radio" name="fixtureChoice" disabled={!pending} checked={r.fixtureChoice === 'new'} onChange={() => setResult({ fixtureChoice: 'new' })} />
              <span>This is a different match — create a new fixture</span>
            </label>
          </div>
        )}
        {!suggestedFixture && !entry.applied.result && <p className="kicks-muted">No matching fixture on this date — one will be created.</p>}
      </section>

      {/* Timeline */}
      {entry.timeline && entry.timeline.length > 1 && (
        <section className="card kicks-card">
          <div className="kicks-card-head">
            <h2>Timeline <span className="kicks-muted">from the app's match clock</span></h2>
            <button type="button" className="ebtn ebtn-secondary ebtn-sm" onClick={() => {
              const txt = entry.timeline.filter(e => e.type !== 'kickoff').map(e => `${e.minute}' ${e.text}`).join('\n');
              navigator.clipboard?.writeText(txt).then(() => setMsg({ ok: true, text: 'Timeline copied — paste it into your post.' }));
            }}>Copy for socials</button>
          </div>
          <ol className="kicks-timeline">
            {entry.timeline.filter(e => e.type !== 'kickoff').map((e, i) => (
              <li key={i} className={`kicks-tl-${e.type}`}><span className="kicks-tl-min">{e.minute}'</span><span>{e.text}</span></li>
            ))}
          </ol>
          <p className="kicks-muted">Goals and assists appear here when they are tapped in the app during the match. Published with the report.</p>
        </section>
      )}

      {/* Players & stats */}
      <section className={`card kicks-card ${entry.applied.stats ? 'kicks-card-done' : ''}`}>
        <div className="kicks-card-head">
          <h2>Players &amp; stats <span className="kicks-muted">({activePlayers.length} of {entry.players.length})</span></h2>
          {entry.applied.stats ? <span className="badge badge-success">On the website ✓</span>
            : pending && entry.players.length > 0 && (
              <button className="ebtn ebtn-primary ebtn-sm" disabled={!!busy || !entry.applied.result}
                title={entry.applied.result ? '' : 'Apply the result first'}
                onClick={() => apply({ stats: true }, 'stats')}>{busy === 'stats' ? 'Applying…' : 'Apply players & stats'}</button>)}
        </div>
        {entry.players.length === 0 ? <p className="kicks-muted">KiCKS sent no player data for this match.</p> : (
          <div className="table-container kicks-table-wrap"><table className="table kicks-table">
            <thead><tr><th>In KiCKS</th><th>On your website</th><th>Role</th><th>G</th><th>A</th><th>Sv</th><th>Def</th><th></th></tr></thead>
            <tbody>{entry.players.map(p => {
              const choice = p.playerId === null ? (p.suggestedPlayerId ?? 'new') : p.playerId;
              return (
                <tr key={p.kicksPlayerId} className={p.dropped ? 'kicks-dropped' : ''}>
                  <td><strong>{p.name}</strong>{p.position ? <span className="kicks-muted"> {p.position}</span> : null}</td>
                  <td>
                    <select className="form-select kicks-select" value={choice} disabled={!pending || p.dropped} onChange={e => setPlayer(p.kicksPlayerId, { playerId: e.target.value })}>
                      <option value="new">＋ New player "{p.name}"</option>
                      {squad.map(s => <option key={s.playerId} value={s.playerId}>{playerName(s)}{s.kicksPlayerId && s.kicksPlayerId !== p.kicksPlayerId ? ' (linked to another)' : ''}</option>)}
                    </select>
                    {p.playerId === null && <span className={`kicks-hint kicks-hint-${p.suggestion}`}>{p.suggestion === 'mapped' ? 'already linked' : p.suggestion === 'by-name' ? 'matched by name' : 'not in your squad'}</span>}
                  </td>
                  <td><select className="form-select kicks-select-sm" value={p.role} disabled={!pending || p.dropped} onChange={e => setPlayer(p.kicksPlayerId, { role: e.target.value as KicksRole })}>
                    {(Object.keys(ROLE_LABEL) as KicksRole[]).map(k => <option key={k} value={k}>{ROLE_LABEL[k]}</option>)}</select></td>
                  {(['goals', 'assists', 'saves', 'defensiveActions'] as const).map(k => (
                    <td key={k}><input type="number" min={0} className="form-input kicks-num" value={p[k]} disabled={!pending || p.dropped || p.role === 'unused'} onChange={e => setPlayer(p.kicksPlayerId, { [k]: Number(e.target.value) } as any)} /></td>))}
                  <td>{pending && <button type="button" className="kicks-linkbtn" onClick={() => setPlayer(p.kicksPlayerId, { dropped: !p.dropped })}>{p.dropped ? 'Restore' : 'Drop'}</button>}</td>
                </tr>);
            })}</tbody>
          </table></div>)}
        <p className="kicks-muted">New players are added <strong>hidden</strong> from the public website; make them visible from Players when you have consent.</p>
      </section>

      {/* Report */}
      <section className={`card kicks-card ${entry.applied.report ? 'kicks-card-done' : ''}`}>
        <div className="kicks-card-head">
          <h2>Match report</h2>
          {entry.applied.report === 'published' ? <span className="badge badge-success">Published ✓</span>
            : entry.applied.report === 'skipped' ? <span className="badge badge-info">Not published</span>
            : pending && entry.report && (
              <span className="kicks-actions">
                <button className="ebtn ebtn-secondary ebtn-sm" disabled={!!busy} onClick={() => apply({ report: 'skip' }, 'skip')}>Don't publish</button>
                <button className="ebtn ebtn-primary ebtn-sm" disabled={!!busy || !entry.applied.result} title={entry.applied.result ? '' : 'Apply the result first'}
                  onClick={() => apply({ report: 'publish' }, 'publish')}>{busy === 'publish' ? 'Publishing…' : 'Publish as news'}</button>
              </span>)}
        </div>
        {!entry.report ? <p className="kicks-muted">No report for this match{pending ? ' yet — if the coach recorded voice notes, the AI report arrives a few minutes after the result' : ''}.</p> : (
          <>
            <label className="kicks-block">Title<input className="form-input" value={entry.report.title} disabled={!pending} onChange={e => setReport({ title: e.target.value })} /></label>
            <label className="kicks-block">Text<textarea className="form-textarea" rows={14} value={entry.report.text} disabled={!pending} onChange={e => setReport({ text: e.target.value })} /></label>
            <p className="kicks-muted">Written by KiCKS's AI from the coach's voice notes and the stats. Edit freely — this is what will appear in your News.</p>
          </>)}
      </section>

      {pending && (
        <div className="kicks-footer">
          <button className="ebtn ebtn-secondary" disabled={!!busy} onClick={dismiss}>Dismiss entry</button>
          <span className="kicks-footer-right">
            {dirty && <span className="kicks-muted">Unsaved edits</span>}
            <button className="ebtn ebtn-secondary" disabled={!!busy || !dirty} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save edits'}</button>
          </span>
        </div>)}
    </div>
  );
}

export default function KicksIntegration() {
  const { id } = useParams();
  return id ? <EntryReview /> : <InboxList />;
}
